import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, inject, Inject, OnInit, } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import {
  DialogTemplateButtonComboStyleEnum
} from '@shared/components/templates/dialog-template/enums/dialog-template-button.enum';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { UserFieldSettingModel } from '@base/modules/rest/bbs-object/model/user-field-setting.model';
import { UserContext } from '@base/services/user-context';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '@shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { Observable, of, switchMap } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface FieldSettingsDialogData {
  object: ObjectDefinitionModel;
  showSetForAllSwitcher?: boolean;
}

interface Result {
  userFieldSettings: UserFieldSettingModel[];
  setForAll: boolean;
}

@UntilDestroy()
@Component({
  selector: 'app-field-settings-dialog',
  templateUrl: './field-settings-dialog.component.html',
  styleUrls: ['./field-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldSettingsDialogComponent implements OnInit {
  form: UntypedFormGroup;
  setForAllFormControl = new UntypedFormControl(false);
  searchTermFormControl = new UntypedFormControl(null);
  DialogTemplateButtonComboStyleEnum = DialogTemplateButtonComboStyleEnum;
  fields: CustomFieldModel[] = [];
  filteredFields: CustomFieldModel[] = [];
  userContext = inject(UserContext);
  searchValue: string;

  constructor(@Inject(MAT_DIALOG_DATA) public data: FieldSettingsDialogData,
              public dialogRef: MatDialogRef<FieldSettingsDialogComponent, Result>,
              private translateService: TranslateService,
              private matDialog: MatDialog) {
  }

  ngOnInit(): void {
    this.fields = this.prepareFields();
    this.filteredFields = this.fields;

    const group = {};
    this.fields
      .forEach(field => {
        group[field.dbName] = new UntypedFormControl(field.visible);
      });
    this.form = new UntypedFormGroup(group);

    this.searchTermFormControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((val: string) => {
        this.searchValue = val;
      });

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.filteredFields.forEach(field => {
          field.visible = this.form.value[field.dbName];
        });
      });
  }

  private prepareFields(): CustomFieldModel[] {
    const objectFields = this.data.object.fields
      .filter(f => !f.hidden)
      .map(f => {
        return {
          ...f,
          visible: this.isFieldVisible(f.dbName),
        };
      });

    const fields: CustomFieldModel[] = [];
    for (const column of this.data.object.columns) {
      const field = objectFields
        .find(f => f.dbName === column);
      if (!!field) {
        fields.push(field);
      }
    }
    for (const field of objectFields) {
      if (!fields.some(f => f.dbName === field.dbName)) {
        fields.push(field);
      }
    }
    let index = 0;
    for (const field of fields) {
      field.columnDisplayOrder = index++;
    }
    return fields;
  }

  private isFieldVisible(name: string): boolean {
    return this.data.object.columns
      .filter(c => c === name)
      .length !== 0;
  }

  setAll(value: boolean): void {
    this.filteredFields.forEach(field => {
      const control = this.form.controls[field.dbName];
      if (control) {
        control.setValue(value);
      } else {
        console.warn('Form control not found', field.dbName);
      }
    });
  }

  get areAllSelected(): boolean {
    return Object.keys(this.form.value)
      .map(o => this.form.value[o])
      .every(item => !!item);
  }

  get formInvalid(): boolean {
    return Object.keys(this.form.value)
      .map(o => this.form.value[o])
      .every(item => !item);
  }

  save(): void {
    const visibleFields = this.filteredFields.filter(field => field.visible);
    const setForAll: boolean = this.setForAllFormControl.value;
    const userFieldSettings: UserFieldSettingModel[] = visibleFields
      .map(field => ({
        fieldName: field.dbName,
        columnDisplayOrder: field.columnDisplayOrder,
        visible: field.visible,
        columnWidth: field.columnWidth,
        defaultColumn: setForAll,
      }));
    of(setForAll)
      .pipe(
        switchMap(val => {
          if (val) {
            return this.openConfirmationDialog();
          }
          return of(true);
        }),
        filter(confirmed => confirmed)
      )
      .subscribe(() => {
        const res = {
          userFieldSettings: userFieldSettings,
          setForAll: setForAll,
        };
        this.dialogRef.close(res);
      });
  }

  private openConfirmationDialog(): Observable<boolean> {
    const data: ConfirmationDialogData = {
      title: this.translateService.instant('TAB_CONFIRMATION_DIALOG.TITLE'),
      description: this.translateService.instant('USER_FIELD_SETTINGS.CONFIRMATION_DIALOG_DESCRIPTION'),
      yesButtonText: this.translateService.instant('TAB_CONFIRMATION_DIALOG.YES_BUTTON'),
      noButtonText: this.translateService.instant('TAB_CONFIRMATION_DIALOG.NO_BUTTON'),
    };
    return ConfirmationDialogComponent.open(this.matDialog, data)
      .afterClosed();
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.fields, event.previousIndex, event.currentIndex);
    this.setFieldsOrder();
    this.filteredFields = this.fields.map(item => item);
  }

  private setFieldsOrder(): void {
    this.fields.forEach((field, index) => {
      field.columnDisplayOrder = index + 1;
    });
  }

  static open(dialog: MatDialog, data: FieldSettingsDialogData): MatDialogRef<FieldSettingsDialogComponent, Result> {
    return dialog.open(FieldSettingsDialogComponent, {
      data,
      width: '600px',
    });
  }
}
